import { UserAddIcon } from "@heroicons/react/outline";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "../../../common/components/Input";
import ModalStandard from "../../../common/components/Modal/ModalStandard";
import { useForm } from "react-hook-form";
export const CONFIRM_CSV_COMPONENT = "teamComponent";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { getCurrentUsersFromCsv } from "../../projectGroups/state/projectGroupsSlice";
import {
  getCurrentProject,
  getCurrentProjectPermissions,
  getProjectPermissionsFromServer,
  setCurrentProjectPermissions,
  setProjectTeam,
} from "../../projects/state/projectsSlice";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import TableComp from "../../../common/components/Table/Table";
import { closeStandardModal } from "../../../common/state/slice/modal/modalSlice";
import type { RootState } from "../../../common/state/store";
import { currentUserSelector } from "../../../common/state/selectors/authSelector";
import { inviteExternalEnduser } from "../state/teamSlice";
import type { IEnduserToBeInserted } from "../state/teamSlice";
import { setAlert } from "../../alert/state/alertsSlice";
import type { Key, MouseEvent } from "react";

interface ICsvFormValues {
  idForInvite: string[];
}

const ParticipantCsv = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const getCurrentUserFromCsv = useAppSelector(getCurrentUsersFromCsv);
  const [listCsvToFilter, setListCsvToFilter] = useState<IEnduserToBeInserted[]>([]);

  const currentProjectPermissions = useAppSelector(getCurrentProjectPermissions);
  const { user, project } = useAppSelector((state: RootState) => {
    return {
      user: currentUserSelector(state),
      project: getCurrentProject(state),
    };
  });

  const projectPermissions = currentProjectPermissions.map((permission) => permission.user);
  const permissionstoString = projectPermissions.map(String);
  const { handleSubmit } = useForm<ICsvFormValues>({
    defaultValues: { idForInvite: permissionstoString },
  });

  const [isCheckAll, setIsCheckAll] = useState(false);
  const [isCheck, setIsCheck] = useState<string[]>([]);
  const handleSelectAll = () => {
    setIsCheckAll(!isCheckAll);
    setIsCheck(listCsvToFilter.map((li) => li.email));
    if (isCheckAll) {
      setIsCheck([]);
    }
  };

  const handleClick = (e: MouseEvent<HTMLInputElement>) => {
    const { id, checked } = e.currentTarget;
    setIsCheck([...isCheck, String(id)]);

    if (!checked) {
      setIsCheck(isCheck.filter((item) => String(item) !== String(id)));
    }
  };

  useEffect(() => {
    setListCsvToFilter(getCurrentUserFromCsv);
  }, [getCurrentUserFromCsv]);

  useEffect(() => {
    if (isCheck.length === listCsvToFilter.length) {
      setIsCheckAll(true);
    } else {
      setIsCheckAll(false);
    }
  }, [isCheck]);

  const getColumnValues = listCsvToFilter.map((member, i: Key | null | undefined) => {
    const filterSneakySneaky = member.email.includes("@sneakysneaky.ua") ? "" : member.email;

    return {
      checkbox: (
        <Checkbox
          key={i}
          checked={isCheck.includes(member.email)}
          id={String(member.email)}
          value={Object.values(member)}
          onClick={(e) => {
            e.stopPropagation();
            handleClick(e);
          }}
        />
      ),
      firstName: member.firstname,
      lastName: member.lastname,
      email: filterSneakySneaky,
    };
  });

  const onSubmit = async () => {
    const filterResultOfNewUser = listCsvToFilter.filter((users) =>
      isCheck.map((mails) => mails).includes(users.email),
    );

    const createObjectFromArray = filterResultOfNewUser.map((user) => {
      const createObject = {
        newUserEmail: user.email,
        newUserFirstName: user.firstname,
        newUserLastName: user.lastname,
      };
      return createObject;
    });

    dispatch(closeStandardModal("add-participant-csv"));

    if (createObjectFromArray.length > 0) {
      let successCount = 0;
      let failCount = 0;

      const promises = createObjectFromArray.map(async (datas) => {
        try {
          const res = await dispatch(
            inviteExternalEnduser({
              componentId: CONFIRM_CSV_COMPONENT,
              inviterId: Number(user?.id),
              projectId: project.id,
              ...datas,
            }),
          );

          if (!inviteExternalEnduser.fulfilled.match(res)) {
            failCount++;
            return;
          }

          const newUser = res.payload.data;
          await dispatch(
            setProjectTeam({
              inviteeId: newUser.user.id,
              projectId: project.id,
              componentId: CONFIRM_CSV_COMPONENT,
              inviterId: Number(user?.id),
            }),
          );
          successCount++;
        } catch {
          failCount++;
        }
      });

      await Promise.all(promises);

      void dispatch(getProjectPermissionsFromServer({ projectId: project.id })).then((res) => {
        if (getProjectPermissionsFromServer.fulfilled.match(res)) {
          dispatch(setCurrentProjectPermissions(res.payload.data));
        }
      });

      if (successCount > 0) {
        dispatch(
          setAlert({
            type: "success",
            msg: t("alerts.csvImportSuccess", { count: successCount }),
          }),
        );
      }
      if (failCount > 0) {
        dispatch(setAlert({ type: "danger", msg: t("alerts.csvImportFail") }));
      }
    }
  };
  const cancelForm = () => {
    dispatch(closeStandardModal("add-participant-csv"));
    setIsCheck([]);
  };

  const colHeaders = [t("general.firstname"), t("general.lastname"), t("general.email")];

  return (
    <ModalStandard className="" id="add-participant-csv" fullScreen={false}>
      <form onSubmit={(e) => e.preventDefault()}>
        <div className="flex mb-3">
          <UserAddIcon className="w-6 h-6 stroke-black mr-3" />
          <h1 className="font-bold text-xl">
            {t("pages.project.inviteParticipants.importedParticipants")}
          </h1>
        </div>

        <h2 className="mb-2">{t("pages.project.inviteParticipants.infoImportedParticipants")}</h2>

        {getColumnValues?.length > 0 && (
          <IsLoading componentId={CONFIRM_CSV_COMPONENT} showSuccess={false}>
            <div className="absolute left-10 top-26 mt-3">
              <div className="flex items-center py-2">
                <input
                  checked={isCheck.length === listCsvToFilter.length}
                  type="checkbox"
                  onChange={handleSelectAll}
                  id="myCheck"
                ></input>
              </div>
            </div>
            <TableComp
              selectFromListInTable={true}
              isAllCollaborators={true}
              isCollaboratorAndParticipant={true}
              rows={getColumnValues}
              colHeaders={colHeaders}
              className="max-h-[45rem] w-full"
              listTofilter={listCsvToFilter}
              setListOfItemsFilter={setListCsvToFilter}
            />
          </IsLoading>
        )}

        <div className="w-full flex justify-end">
          <button
            type="button"
            className="btn-alternative-outline mt-2"
            onClick={() => cancelForm()}
          >
            {t("general.cancel")}
          </button>
          {getColumnValues?.length > 0 && (
            <button
              disabled={isCheck.length === 0}
              type="button"
              onClick={() => handleSubmit(onSubmit)()}
              className="btn-primary-fill mt-2 ml-2"
            >
              {t("general.confirm")}
            </button>
          )}
        </div>
      </form>
    </ModalStandard>
  );
};

export default ParticipantCsv;
